import { keyNeedsReview, specialItems, suggestedInventory, unitsForKey } from '../data/inventory';
import { packingCatalog } from '../data/packingCatalog';
import type { PricingConfig } from '../data/pricingConfig';
import type { AccessInfo, Bedrooms, CrewDecision, FunnelData, QuoteResult } from '../types/funnel';
import { screenRoute } from './serviceArea';

type StairLevel = 0 | 1 | 2 | 3;

export function inventoryUnits(inv: Record<string, number>, customCount = 0): number {
  return Object.entries(inv).reduce((s, [key, q]) => s + unitsForKey(key) * q, 0) + customCount * 1;
}

/** Office moves are sized by office size, not bedrooms. Maps to the closest planning size internally. */
export function effectiveSize(d: Pick<FunnelData, 'homeType' | 'officeSize' | 'bedrooms'>): Bedrooms {
  if (d.homeType === 'office') return d.officeSize === 'large' ? '5' : d.officeSize === 'medium' ? '3' : '1';
  return d.bedrooms ?? '1';
}

/**
 * Planning suggestion only, from internal inventory units. Not cubic feet, payload or a capacity guarantee.
 * Thresholds are placeholders until the owner's real fleet catalog is connected.
 */
export function suggestTruck(units: number): '16' | '20' | '26' {
  if (units <= 22) return '16';
  if (units <= 40) return '20';
  return '26';
}

function usesElevator(a: AccessInfo): boolean {
  return a.floor !== 'ground' && a.method !== 'stairs';
}

function stairLevel(a: AccessInfo): StairLevel {
  if (a.floor === 'ground' || a.method !== 'stairs') return 0;
  return a.floor === 'second' ? 1 : a.floor === 'third' ? 2 : 3;
}

/** Owner guidance (Oct 6): recommend once, honor a 2-mover preference when dispatch can support it. */
export function recommendCrew(bedrooms: Bedrooms, stairs: StairLevel, elevator: boolean): Omit<CrewDecision, 'used'> {
  let rec: Omit<CrewDecision, 'used'>;
  switch (bedrooms) {
    case 'few':
    case 'studio':
    case '1':
      rec = { recommended: 2, reviewRequired: false, reasonKey: 'cr_1' };
      break;
    case '2':
      rec = stairs >= 2 ? { recommended: 3, reviewRequired: false, reasonKey: 'cr_2_third' } : { recommended: 2, reviewRequired: false, reasonKey: 'cr_2' };
      break;
    case '3':
      rec = { recommended: 3, reviewRequired: false, reasonKey: stairs >= 1 ? 'cr_3_upper' : 'cr_3' };
      break;
    default:
      rec = { recommended: 3, reviewRequired: true, reasonKey: 'cr_45' };
  }
  if (stairs === 3) rec = { ...rec, reviewRequired: true, reasonKey: 'cr_stairsHigh' };
  if (elevator) rec = { ...rec, reviewRequired: true, reasonKey: rec.reasonKey === 'cr_45' ? 'cr_45' : 'cr_elevator' };
  return rec;
}

const roundHalf = (n: number) => Math.round(n * 2) / 2;

export function evaluateQuote(d: FunnelData, cfg: PricingConfig): QuoteResult {
  const bedrooms: Bedrooms = effectiveSize(d);
  const o = d.origin;
  const t = d.destination;
  const so = stairLevel(o);
  const st = stairLevel(t);
  const worst = Math.max(so, st) as StairLevel;
  const elevatorEnds = [o, t].filter(usesElevator).length;
  const rec = recommendCrew(bedrooms, worst, elevatorEnds > 0);
  const used = d.crewPreference ?? rec.recommended;
  const crew: CrewDecision = { ...rec, used };
  const reviewReasons: string[] = [];

  const base = cfg.baseHoursByBedroom[bedrooms];
  const upl = (s: StairLevel) => (s === 0 ? 0 : s === 1 ? cfg.stairsUpliftHours.second : cfg.stairsUpliftHours.third + (s === 3 ? 1 : 0));
  const stairsHours = Math.max(upl(so), upl(st)) + Math.min(upl(so), upl(st)) * 0.5;
  const elevatorHours = elevatorEnds === 0 ? 0 : Math.min(1, cfg.elevatorAllowanceHours * elevatorEnds);
  const co = cfg.carryPlanningHours[o.carryBand];
  const ct = cfg.carryPlanningHours[t.carryBand];
  const carryHours = Math.max(co, ct) + Math.min(co, ct) * 0.5;

  const customCount = d.customItems.reduce((s, c) => s + c.qty, 0);
  const units = inventoryUnits(d.inventory, customCount);
  const refUnits = inventoryUnits(suggestedInventory[bedrooms]);
  const ratio = refUnits > 0 && units > 0 ? units / refUnits : 1;
  const invFactor = Math.min(0.4, Math.max(-0.25, ratio - 1));

  // Fewer movers than recommended take longer; more movers go faster (planning only).
  const crewFactor = used < rec.recommended ? 1.3 : used > rec.recommended ? 0.8 : 1;

  const packedBoxes = packingCatalog.filter((p) => p.kind === 'packed').reduce((s, p) => s + (d.packingProducts[p.id] ?? 0) * p.boxes, 0);
  const packingHours = d.packing === 'professional' ? (packedBoxes / 10) * cfg.packingCrewHoursPer10Boxes * (2 / used) : 0;

  const work = (base * (1 + invFactor) + stairsHours + elevatorHours + carryHours) * crewFactor;
  // Packing labor is billed through the packing selection (when priced) — not added again to hourly labor.
  const serviceLow = roundHalf(Math.max(cfg.minimumServiceHours, work));
  const serviceHigh = serviceLow + cfg.rangeSpreadHours;

  const zone = screenRoute(d.originZip, d.destZip);
  if (zone === 'outside' || zone === 'review') reviewReasons.push('rr_zone');
  if (d.specialItems.some((id) => specialItems.find((s) => s.id === id)?.requiresReview)) reviewReasons.push('rr_special');
  if (ratio > 1.4) reviewReasons.push('rr_bigInventory');
  if (bedrooms === '5') reviewReasons.push('rr_large');
  if (elevatorEnds > 0) reviewReasons.push('rr_elevator');
  if (worst === 3) reviewReasons.push('rr_stairsHigh');
  if (o.carryBand === 'over_100' || t.carryBand === 'over_100') reviewReasons.push('rr_longCarry');
  if (used < rec.recommended) reviewReasons.push('rr_crewPref');
  if (bedrooms === '4' && worst >= 1) reviewReasons.push('rr_large');
  const stops = d.stops ?? [];
  if (stops.length > 0) reviewReasons.push('rr_stops');
  if (Object.entries(d.inventory).some(([k, q]) => q > 0 && keyNeedsReview(k))) reviewReasons.push('rr_variantReview');
  const truckSuggestion = suggestTruck(units);
  if (d.truckPreference && d.truckPreference !== 'help' && Number(d.truckPreference) < Number(truckSuggestion)) reviewReasons.push('rr_truckSmall');

  const hourlyRate = cfg.ratesApproved ? cfg.rates[used] ?? null : null;
  const laborLow = hourlyRate !== null ? hourlyRate * (serviceLow + cfg.travelHours) : null;
  const laborHigh = hourlyRate !== null ? hourlyRate * (serviceHigh + cfg.travelHours) : null;

  const selected = packingCatalog.filter((p) => (d.packingProducts[p.id] ?? 0) > 0);
  const packingSelected = d.packing === 'professional' && selected.length > 0;
  const packingPriced = !packingSelected || selected.every((p) => p.price !== null);

  const hash = hashString(
    JSON.stringify([
      cfg.version, bedrooms, d.homeType, d.officeSize, o, t, d.inventory, d.customItems, d.specialItems, d.packing, d.packingProducts,
      d.originZip, d.destZip, stops.map((s) => [s.zip, s.role, s.access]), d.services, d.truckPreference, used, d.moveDate, d.arrival,
    ]),
  );

  return {
    configVersion: cfg.version,
    crew,
    serviceLow,
    serviceHigh,
    travelHours: cfg.travelHours,
    minimumHours: cfg.minimumServiceHours,
    packingHours: roundHalf(packingHours),
    hourlyRate,
    laborLow,
    laborHigh,
    surchargePercent: cfg.surchargePercent,
    surchargeBaseApproved: cfg.surchargeBaseApproved,
    packingSelected,
    packingPriced,
    deposit: cfg.depositAmount,
    remainingLow: laborLow !== null ? laborLow - cfg.depositAmount : null,
    remainingHigh: laborHigh !== null ? laborHigh - cfg.depositAmount : null,
    reviewReasons: Array.from(new Set(reviewReasons)),
    zone,
    units: Math.round(units * 10) / 10,
    truckSuggestion,
    hash,
  };
}

function hashString(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36).toUpperCase();
}
